import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Package, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { cartItemApi } from '@/api/commercial/cart-items';
import { catalogApi } from '@/api/commercial/catalog';
import { creditAnalysisApi } from '@/api/commercial/credit-analysis';
import { PriceTableStatus, priceTableApi } from '@/api/commercial/price-tables';
import type { ProposalPayment } from '@/api/commercial/proposals';
import { Button } from '@/components/ui/button';
import { useCartCalculate } from '@/hooks/commercial/use-cart-calculate';
import {
  type PaymentFormState,
  validateCartStep,
} from '@/hooks/commercial/use-cart-step-validation';
import { usePaymentConfigCurrent } from '@/hooks/commercial/use-payment-config';
import { toast } from '@/hooks/use-toast';
import {
  type IProposalCartItem,
  NEGOTIATION_TYPES,
  NegotiationType,
} from '@/types/commercial/cart';
import { calculateCartSummaryCategories } from '@/utils/commercial/cart-summary-calculations';
import { StepCartEmptyState } from './step-cart-empty-state';
import { StepCartItem } from './step-cart-item';
import { StepCartPaymentPanel } from './step-cart-payment-panel';

function getNegotiationLabel(type: NegotiationType): string {
  return NEGOTIATION_TYPES.find((n) => n.id === type)?.name ?? type;
}

function groupCartItemsByType(
  items: IProposalCartItem[],
): Map<NegotiationType, IProposalCartItem[]> {
  return items.reduce((acc, item) => {
    const type = item.negotiationType;
    if (!acc.has(type)) acc.set(type, []);
    acc.get(type)?.push(item);
    return acc;
  }, new Map<NegotiationType, IProposalCartItem[]>());
}

interface StepCartContainerProps {
  proposalId?: string;
  savedPayment?: ProposalPayment | null;
  onOpenAddItems: () => void;
  onEditItem: (item: IProposalCartItem) => void;
  onValidationChange?: (isValid: boolean, message: string | null) => void;
  onSavePaymentRef?: (saveFn: () => Promise<void>) => void;
}

export function StepCartContainer({
  proposalId,
  savedPayment,
  onOpenAddItems,
  onEditItem,
  onValidationChange,
  onSavePaymentRef,
}: StepCartContainerProps) {
  const queryClient = useQueryClient();
  const [removingItemId, setRemovingItemId] = useState<string | null>(null);
  const [paymentFormState, setPaymentFormState] = useState<PaymentFormState>({
    paymentType: '',
    dueDay: '',
    installmentCount: '',
    hasEntrance: false,
    entrancePercent: '',
  });

  const {
    data: cartItems,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['cart-items', proposalId],
    queryFn: () => cartItemApi.findMany(proposalId!),
    enabled: !!proposalId,
  });

  const { data: calculateData } = useCartCalculate(proposalId);

  const { data: creditAnalysis } = useQuery({
    queryKey: ['credit-analysis', proposalId],
    queryFn: () => creditAnalysisApi.findByProposal(proposalId!),
    enabled: !!proposalId,
  });

  const invalidateCartQueries = () => {
    if (!proposalId) return;
    queryClient.invalidateQueries({ queryKey: ['cart-items', proposalId] });
    queryClient.invalidateQueries({ queryKey: ['cart-calculate', proposalId] });
    queryClient.invalidateQueries({ queryKey: ['proposal-configurations', proposalId] });
  };

  const removeMutation = useMutation({
    mutationFn: ({ proposalId: pid, itemId }: { proposalId: string; itemId: string }) =>
      cartItemApi.removeOne(pid, itemId),
    onSuccess: () => {
      invalidateCartQueries();
      toast({ title: 'Item removido do carrinho' });
    },
    onError: () => {
      toast({ title: 'Erro ao remover item', variant: 'destructive' });
    },
    onSettled: () => {
      setRemovingItemId(null);
    },
  });

  const [updatingDiscountId, setUpdatingDiscountId] = useState<string | null>(null);

  const updateDiscountMutation = useMutation({
    mutationFn: ({
      proposalId: pid,
      itemId,
      payload,
    }: {
      proposalId: string;
      itemId: string;
      payload: { discountPercent?: number; connectivityDiscountPercent?: number };
    }) => cartItemApi.updateOne(pid, itemId, payload),
    onSuccess: () => {
      invalidateCartQueries();
    },
    onError: () => {
      toast({ title: 'Erro ao atualizar desconto', variant: 'destructive' });
    },
    onSettled: () => {
      setUpdatingDiscountId(null);
    },
  });

  const catalogResults = useQueries({
    queries: Object.values(NegotiationType).map((type) => ({
      queryKey: ['catalog', type],
      queryFn: () => catalogApi.findMany({ negotiationType: type }),
      enabled: !!proposalId,
    })),
  });

  const { data: activePriceTable } = useQuery({
    queryKey: ['price-tables', 'active'],
    queryFn: async () => {
      const result = await priceTableApi.findMany({ status: PriceTableStatus.ACTIVE, limit: 1 });
      return result.docs[0] ?? null;
    },
    enabled: !!proposalId,
  });

  const { data: paymentConfig } = usePaymentConfigCurrent();
  const minEntradaPercent = paymentConfig?.minEntradaPercent ?? 0;

  const connPriceLookup = useMemo(() => {
    const map = new Map<string, number>();
    const simPayment = activePriceTable?.simcardPayment?.[0];
    if (!simPayment) return map;
    for (const result of catalogResults) {
      if (!result.data) continue;
      for (const item of result.data) {
        if (item.hasSimCard) {
          map.set(item.id, simPayment.priceInBundleInCents);
        }
      }
    }
    return map;
  }, [activePriceTable, catalogResults]);

  const nameLookup = useMemo(() => {
    const map = new Map<string, string>();
    for (const result of catalogResults) {
      if (result.data) {
        for (const item of result.data) {
          map.set(item.id, item.name);
        }
      }
    }
    return map;
  }, [catalogResults]);

  const items = calculateData?.items ?? cartItems ?? [];
  const hasItems = items.length > 0;
  const breakdown = calculateData?.breakdown ?? null;
  const totalDiscountInCents = calculateData?.paymentSummary?.totalDiscountInCents ?? 0;
  const paymentSummary = calculateData?.paymentSummary ?? null;
  const isRecurringOnly = calculateData?.isRecurringOnly ?? false;
  const summaryPaymentType =
    paymentSummary?.paymentType ??
    (paymentFormState.paymentType === 'INSTALLMENTS' ? 'INSTALLMENTS' : 'CASH');
  const cartSummaryCategories = calculateCartSummaryCategories(items, summaryPaymentType);

  const allowedPaymentMethods = creditAnalysis?.allowedPaymentMethods ?? ['CASH', 'INSTALLMENTS'];
  const canUseInstallments = creditAnalysis?.canUseInstallments ?? true;
  const installmentsRestrictionReason = creditAnalysis?.installmentsRestrictionReason ?? null;

  const validation = useMemo(
    () =>
      validateCartStep(items.length, paymentFormState, allowedPaymentMethods, minEntradaPercent),
    [items.length, paymentFormState, allowedPaymentMethods, minEntradaPercent],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run when validation result changes
  useEffect(() => {
    onValidationChange?.(validation.isStepValid, validation.message);
  }, [validation.isStepValid, validation.message]);

  return (
    <div className="flex gap-6">
      <div className="flex-1">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Package className="mr-2 h-5 w-5 animate-pulse text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Carregando carrinho...</p>
          </div>
        )}

        {!isLoading && !hasItems && !isError && <StepCartEmptyState />}

        {isError && (
          <div className="flex items-center justify-center py-12">
            <p className="text-sm text-destructive">Erro ao carregar itens do carrinho.</p>
          </div>
        )}

        {!isLoading && hasItems && (
          <div className="space-y-4">
            {[...groupCartItemsByType(items).entries()].map(([type, groupItems]) => (
              <div key={type} className="rounded-md border p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {getNegotiationLabel(type)}
                </p>
                <div className="space-y-3">
                  {groupItems.map((item) => (
                    <StepCartItem
                      key={item.id}
                      item={item}
                      nameLookup={nameLookup}
                      connPriceLookup={connPriceLookup}
                      isUpdatingDiscount={updatingDiscountId === item.id}
                      onEdit={() => onEditItem(item)}
                      onRemove={() => {
                        setRemovingItemId(item.id);
                        removeMutation.mutate({
                          proposalId: item.proposalId,
                          itemId: item.id,
                        });
                      }}
                      onUpdateDiscount={(field, value) => {
                        setUpdatingDiscountId(item.id);
                        updateDiscountMutation.mutate({
                          proposalId: item.proposalId,
                          itemId: item.id,
                          payload: { [field]: value },
                        });
                      }}
                      isRemoving={removingItemId === item.id}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="w-[300px] space-y-4">
        <Button className="w-full bg-black text-white hover:bg-gray-800" onClick={onOpenAddItems}>
          <Plus className="mr-2 h-4 w-4" />
          Adicionar itens ao carrinho
        </Button>

        <StepCartPaymentPanel
          proposalId={proposalId}
          savedPayment={savedPayment}
          breakdown={breakdown}
          totalDiscountInCents={totalDiscountInCents}
          cartSummaryCategories={cartSummaryCategories}
          paymentSummary={paymentSummary}
          isRecurringOnly={isRecurringOnly}
          hasCartItems={hasItems}
          allowedPaymentMethods={allowedPaymentMethods}
          canUseInstallments={canUseInstallments}
          installmentsRestrictionReason={installmentsRestrictionReason}
          minEntrancePercent={minEntradaPercent}
          onPaymentStateChange={setPaymentFormState}
          onSavePaymentRef={onSavePaymentRef}
        />

        {hasItems && (
          <p className="text-xs text-muted-foreground text-center">
            {items.length} {items.length === 1 ? 'item' : 'itens'} no carrinho
          </p>
        )}
      </div>
    </div>
  );
}
